import React, { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import IconGoogle from './IconGoogle'
import IconLogoSUMM from './IconLogoSUMM'
import IconLogoSummText from './IconLogoSummText'
import IconMicrosoft from './IconMicrosoft'

// Utils
import { glyphs } from './glyphs'

// Types
import { IconType } from './IconType'

// Styles
import { fastTransition } from '../../styles/effects'

export interface IProps {
  type: IconType
  // 12, 16, 20 or 24 for interface icons (knowledge/design/2026-09-22-icon-grid-and-safe-area.md)
  size: number
  // A theme colour name (`textLight`) or any CSS colour
  color?: string
  // Only the SUMM logo is drawn in more than one colour
  secondaryColor?: string
  thirdColor?: string
  direction?: 'up' | 'down'
  variant?: boolean
  className?: string
  onClick?: () => void
}

const Icon = ({
  type,
  size,
  color = 'primary',
  secondaryColor,
  thirdColor,
  direction,
  variant,
  className,
  onClick,
}: IProps) => {
  const theme = useContext(ThemeContext)
  const fill = theme?.colors[color] ?? color
  const byKey = glyphs as Record<string, { viewBox: string; d: string }>
  const glyph =
    byKey[[type, direction, variant && 'variant'].filter(Boolean).join('-')] ??
    byKey[type]

  return (
    <IconWrapper className={className} onClick={onClick} size={size}>
      {glyph && (
        <svg viewBox={glyph.viewBox} width={size} height={size} aria-hidden>
          <path d={glyph.d} fill={fill} />
        </svg>
      )}
      {type === 'LogoSUMM' && (
        <IconLogoSUMM
          size={size}
          color={fill}
          secondColor={secondaryColor}
          thirdColor={thirdColor}
        />
      )}
      {type === 'LogoSummText' && <IconLogoSummText size={size} color={fill} />}
      {type === 'Google' && <IconGoogle size={size} />}
      {type === 'Microsoft' && <IconMicrosoft size={size} />}
    </IconWrapper>
  )
}

export const IconWrapper = styled.span<Pick<IProps, 'size'>>`
  position: relative;
  display: inline-block;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
  margin-right: 8px;
  flex-shrink: 0;

  &:last-child {
    margin-right: 0;
  }

  svg {
    position: absolute;
    top: 0;
    left: 0;

    path {
      transition: fill ${fastTransition};
    }
  }
`

export default Icon
